/**
 * layoutDag.ts — v0.2 Pass 1 + Pass 2 orchestrator.
 *
 * Two layered passes (per spec rev 3):
 *   Pass 1 (CONTENT) — `topologyWalk` discovers anchors, walks
 *     children recursively, dispatches by NODE_GROUP. Mutates node
 *     positions in place.
 *   Pass 2 (DECORATION) — `layoutGroups.decorationPass` positions /
 *     creates chrome (phase_stack_group, turn_separator, artifact_tile,
 *     orphan_tray) relative to content. Returns turnBoundingBoxes for
 *     LayoutResult.
 *
 * Camp companions (spine_root_marker anchorRoots) ride LEFT-side lanes at the
 * TOP of the trail (CAMP_LANE_DX per camp, in invitedAt order) — never the x=0
 * spine column, so the trail-head fork fans aside instead of slicing the spine
 * (LEFT keeps clear of the minimap, which owns the top-right corner by design).
 * Per plan-triad N10: exposes window.__chatdagDiag singleton for production debug.
 *
 * NOTE: Phase 3 additive — this file is NOT wired into ChatDagCanvas.
 * Phase 4 cutover flips Canvas to import from here (Task 4.2).
 */
import type { Node, Edge } from 'reactflow';

import {
  CAMP_LANE_DX,
  INTER_CHAIN_GAP,
} from './layoutFans';
import {
  discoverAnchors,
  makeWalker,
  type WalkCtx,
} from './topologyWalk';
import { decorationPass, type BBox } from './layoutGroups';
import type { ChatDagNodeType, GroupingDim } from '../types';

export type LayoutResult = {
  nodes: Node[];
  edges: Edge[];
  turnBoundingBoxes: Map<string, BBox>;
};

// Fallback dims for nodes without an explicit entry in the dimensions
// map passed by the caller. Mirrors COMPACT_DEFAULT from chatDagLayout
// (which gets deleted at Phase 4) — small duplication, captured as a
// refactor followup (consolidate into layoutFans.ts).
const DIM_FALLBACK: Partial<Record<ChatDagNodeType, { w: number; h: number }>> = {
  turn_marker: { w: 260, h: 120 },
  llm_round: { w: 220, h: 95 },
  tool_call: { w: 280, h: 90 },
  peer: { w: 240, h: 140 },
  fan_collapsed_chip: { w: 220, h: 70 },
  collapsed_phase_stack: { w: 320, h: 90 },
  turn_separator: { w: 540, h: 24 },
  artifact_tile: { w: 320, h: 80 },
  orphan_tray: { w: 420, h: 110 },
  spine_root_marker: { w: 180, h: 68 },  // SP3a — name+task+arrival+target (4 lines); was h:36
  peer_response: { w: 280, h: 120 },
  spine_fan_chip: { w: 240, h: 56 },
  cycle_error: { w: 240, h: 48 },
  spine_chat_panel: { w: 340, h: 420 },  // SP-3a — composite intent-chat panel
};

function makeDimsOf(
  dimensions: Map<string, { w: number; h: number }>,
): (n: Node) => { w: number; h: number } {
  return (n: Node) => {
    const measured = dimensions.get(n.id);
    if (measured) return measured;
    const explicitW = (n as any).width as number | undefined;
    const explicitH = (n as any).height as number | undefined;
    if (explicitW != null && explicitH != null) return { w: explicitW, h: explicitH };
    const kind = n.type as ChatDagNodeType | undefined;
    const fallback = kind ? DIM_FALLBACK[kind] : undefined;
    return fallback ?? { w: 200, h: 100 };
  };
}

/**
 * C2.2 — post-walk pass: snap each merge_node to its MAIN-spine column (the
 * source of its non-'merge' edge) at a y below the bottom of ALL its parents,
 * so the convergence reads cleanly on the main track with the camp edge fanning
 * in from the side. Gated on the B1 per-parent edges (chatDagDataProcessor).
 */
export function repositionMergeNodes(
  nodes: Node[],
  edges: Edge[],
  byId: Map<string, Node>,
  dimsOf: (n: Node) => { w: number; h: number },
): void {
  // C2.3 — a switchback's reconsidered continuation re-blazes from an EARLIER node
  // whose column the live forward path still occupies. Offset it into its own lane
  // (320 = node width 260 + 60 gutter; one lane sits inside PERSONA_COL_GAP=720),
  // incrementing per switchback in document order so serial reconsiders off one
  // trail don't stack. Unbounded by design (MVP) — edge-bundling/lane-routing
  // relief is a fast-follow (spec §0.1 Layout).
  const SWITCHBACK_LANE_DX = 320;
  const parents = new Map<string, { sources: string[]; mainSource?: string; isSwitchback?: boolean }>();
  for (const e of edges) {
    const rec = parents.get(e.target) ?? { sources: [] };
    rec.sources.push(e.source);
    if (e.type !== 'merge') rec.mainSource = e.source;  // main spine / reconsider origin
    if (e.type === 'reconsider') rec.isSwitchback = true;
    parents.set(e.target, rec);
  }
  let switchbackLane = 0;
  for (const n of nodes) {
    if (n.type !== 'merge_node') continue;
    const rec = parents.get(n.id);
    if (!rec || rec.sources.length === 0) continue;
    let maxBottom = -Infinity;
    for (const sid of rec.sources) {
      const p = byId.get(sid);
      if (!p) continue;
      maxBottom = Math.max(maxBottom, p.position.y + dimsOf(p).h);
    }
    if (maxBottom === -Infinity) continue;
    const mainParent = rec.mainSource ? byId.get(rec.mainSource) : undefined;
    const baseX = mainParent ? mainParent.position.x : n.position.x;
    // Forward (C2.2) merge → origin column (baseX); switchback → its own lane.
    const x = rec.isSwitchback ? baseX + (++switchbackLane) * SWITCHBACK_LANE_DX : baseX;
    n.position = { x, y: maxBottom + INTER_CHAIN_GAP };
  }
}

export function layoutDag(
  nodes: Node[],
  edges: Edge[],
  dimensions: Map<string, { w: number; h: number }> = new Map(),
  // _focalTurnId + _inFlightStartedAtMs reserved for parity with
  // chatDagLayout signature; currently unused by the v0.2 pipeline.
  _focalTurnId: string | null = null,
  _inFlightStartedAtMs: number | null = null,
  runtime?: {
    collapsedFanIds?: ReadonlySet<string>;
    activeDim?: GroupingDim;
    expandedCardSpineIds?: ReadonlySet<string>;
  },
): LayoutResult {
  const childrenByParent = new Map<string, string[]>();
  for (const e of edges) {
    if (!childrenByParent.has(e.source)) childrenByParent.set(e.source, []);
    childrenByParent.get(e.source)!.push(e.target);
  }
  const byId = new Map<string, Node>();
  for (const n of nodes) byId.set(n.id, n);
  const dimsOf = makeDimsOf(dimensions);

  const { spineAnchors, anchorRoots } = discoverAnchors(nodes, edges);

  const ctx: WalkCtx = { childrenByParent, byId, dimsOf, nodes, runtime };
  const walk = makeWalker(ctx);

  // SHARED visited set across both for-loops (architect R2-7) — prevents
  // double-walking shared subgraphs in partial-load scenarios.
  const visited = new Set<string>();
  let cursorY = 0;

  for (const anchor of spineAnchors) {
    anchor.position = { x: 0, y: cursorY };
    cursorY = walk(anchor, 0, cursorY, 0, visited);
    cursorY += INTER_CHAIN_GAP;
  }
  // Camp companions ride LEFT-side lanes at the TOP of the trail — not stacked
  // below the spine. Each spine_root_marker takes its own lane LEFT of the x=0
  // spine (CAMP_LANE_DX per camp, incrementing in invitedAt order), anchored at the
  // trail top so its trail-head fork edge fans cleanly aside instead of running
  // straight down the spine column. LEFT (not right): the ChatMap minimap + controls
  // own the top-right corner by design. An arrived camp's own sub-spine flows DOWN
  // from its chip within the lane. (Was: x=colFor(persona) — a lone camp resolved to
  // x=0, the spine's column — at y=cursorY, the bottom; the fork then sliced through
  // every you/assistant node and sank lower with each turn.)
  let campLane = 0;
  for (const ar of anchorRoots) {
    const laneX = -(++campLane) * CAMP_LANE_DX;
    ar.position = { x: laneX, y: 0 };
    walk(ar, laneX, 0, 0, visited);
  }

  // C2.2 — gated on B1's per-parent edges: snap merge nodes onto the main column
  // below all parents (the walker positioned them from one parent only).
  repositionMergeNodes(nodes, edges, byId, dimsOf);

  const turnBoundingBoxes = decorationPass(
    nodes, edges, runtime?.activeDim, runtime?.expandedCardSpineIds,
  );

  if (typeof window !== 'undefined') {
    (window as any).__chatdagDiag = {
      nodeCount: nodes.length,
      edgeCount: edges.length,
      anchorRootsCount: anchorRoots.length,
      spineAnchorsCount: spineAnchors.length,
      turnBoundingBoxesCount: turnBoundingBoxes.size,
      lastLayoutAt: new Date().toISOString(),
    };
  }

  return { nodes, edges, turnBoundingBoxes };
}
